import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  mhvillageApi,
  type CreateExternalMappingPayload,
  type UpdateExternalMappingPayload
} from "api/mhvillage";

const externalMappingKeys = {
  all: ["MHVILLAGE_EXTERNAL_MAPPINGS"],
  lists: () => [...externalMappingKeys.all, "LIST"]
};

export const useExternalMappings = () =>
  useQuery({
    queryKey: externalMappingKeys.lists(),
    queryFn: () => mhvillageApi.listExternalMappings()
  });

export const useCreateExternalMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...externalMappingKeys.all, "CREATE"],
    mutationFn: (payload: CreateExternalMappingPayload) =>
      mhvillageApi.createExternalMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMappingKeys.lists() });
    }
  });
};

export const useUpdateExternalMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...externalMappingKeys.all, "UPDATE"],
    mutationFn: ({
      id,
      payload
    }: {
      id: number;
      payload: UpdateExternalMappingPayload;
    }) => mhvillageApi.updateExternalMapping(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMappingKeys.lists() });
    }
  });
};

export const useDeleteExternalMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...externalMappingKeys.all, "DELETE"],
    mutationFn: (id: number) => mhvillageApi.deleteExternalMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMappingKeys.lists() });
    }
  });
};

export const useImportExternalMappings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...externalMappingKeys.all, "IMPORT"],
    mutationFn: (file: File) => mhvillageApi.importExternalMappings(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMappingKeys.lists() });
    }
  });
};

// Read-only — parses the file server-side without writing anything, so nothing to invalidate.
export const useImportExternalMappingsPreview = () =>
  useMutation({
    mutationKey: [...externalMappingKeys.all, "IMPORT_PREVIEW"],
    mutationFn: (file: File) => mhvillageApi.previewImportExternalMappings(file)
  });
